import { useState, useMemo, useRef, useEffect } from 'react'
import { locateChunk } from '../../utils/locateChunk'
import './ReferencePanel.css'

const MAX_LABEL_LEN = 60
const PREVIEW_LEN = 240
const PALETTE_SIZE = 5
const HUE_BY_VISIBLE_RANK = [230, 175, 295, 50, 130]

function visibleRank(rank) {
  return ((rank - 1) % PALETTE_SIZE) + 1
}

function hueForVisibleRank(n) {
  return HUE_BY_VISIBLE_RANK[(n - 1) % HUE_BY_VISIBLE_RANK.length]
}

function makeLabel(chunk, rank) {
  const ix =
    chunk.chunk_index !== undefined && chunk.chunk_index !== null
      ? chunk.chunk_index
      : rank
  const raw = (chunk.content || '').trim().replace(/\s+/g, ' ')
  const trimmed =
    raw.length > MAX_LABEL_LEN ? raw.slice(0, MAX_LABEL_LEN) + '…' : raw
  return `#${ix} — "${trimmed}"`
}

function makePreview(text) {
  if (!text) return ''
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > PREVIEW_LEN ? flat.slice(0, PREVIEW_LEN) + '…' : flat
}

function formatPercent(score) {
  return (score * 100).toFixed(1) + '%'
}

// Linear segmentation across the entire parent text. Chunks are character
// ranges in the flat parent_content, never split by block boundaries.
function segmentText(text, locatedChildren) {
  const len = text.length
  const spans = locatedChildren
    .filter(c => c.flatSpan)
    .map(c => ({
      child: c,
      a: Math.max(0, c.flatSpan.start),
      b: Math.min(len, c.flatSpan.end),
    }))
    .filter(s => s.b > s.a)
  if (spans.length === 0) return [{ text, children: [] }]
  const points = new Set([0, len])
  for (const s of spans) {
    points.add(s.a)
    points.add(s.b)
  }
  const sorted = [...points].sort((a, b) => a - b)
  const segs = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]
    const b = sorted[i + 1]
    if (a === b) continue
    const covering = spans
      .filter(s => s.a <= a && s.b >= b)
      .map(s => s.child)
    segs.push({ text: text.slice(a, b), children: covering })
  }
  return segs
}

function HighlightedBody({ parentContent, chunks, activeId, setActiveId }) {
  const segments = useMemo(
    () => segmentText(parentContent, chunks),
    [parentContent, chunks]
  )

  // A fresh Set per render — first time we see each chunk id wins the anchor.
  const seen = new Set()
  const registerAnchor = id => {
    if (seen.has(id)) return false
    seen.add(id)
    return true
  }

  return (
    <div className="prose">
      {segments.map((seg, i) => {
        if (seg.children.length === 0) {
          return <span key={i}>{seg.text}</span>
        }
        const sortedByRank = [...seg.children].sort((a, b) => a.rank - b.rank)
        const top = sortedByRank[0]
        const overlap = seg.children.length > 1
        const anyActive = activeId != null
        const activeChild = anyActive
          ? seg.children.find(c => c.id === activeId)
          : null
        const thisActive = activeChild != null
        const dataActive = !anyActive ? '' : thisActive ? 'on' : 'off'
        // When the segment is the active hover target, color it with the
        // active chunk's rank so overlaps reflect the user's selection
        // rather than the top-ranked chunk in the overlap.
        const displayChunk = thisActive ? activeChild : top
        let anchorAttr
        for (const c of sortedByRank) {
          if (registerAnchor(c.id)) {
            anchorAttr = c.id
            break
          }
        }
        const titleText = sortedByRank
          .map(c => `#${c.rank} · ${c.score.toFixed(2)} — ${c.label}`)
          .join(' + ')
        const overlapStyle = overlap
          ? {
              '--ovl-hue-a': hueForVisibleRank(visibleRank(sortedByRank[0].rank)),
              '--ovl-hue-b': hueForVisibleRank(
                visibleRank((sortedByRank[1] || sortedByRank[0]).rank)
              ),
            }
          : undefined
        return (
          <span
            key={i}
            className="hl"
            data-rank={visibleRank(displayChunk.rank)}
            data-overlap={overlap ? 'true' : undefined}
            data-active={dataActive}
            data-anchor={anchorAttr}
            style={overlapStyle}
            onMouseEnter={() => setActiveId(top.id)}
            title={titleText}
          >
            {seg.text}
          </span>
        )
      })}
    </div>
  )
}

function useChipPositions(containerRef, chunks, deps = []) {
  const [positions, setPositions] = useState({})
  useEffect(() => {
    if (!containerRef.current) return
    let cancelled = false
    const measure = () => {
      if (cancelled) return
      const cont = containerRef.current
      if (!cont) return
      const cr = cont.getBoundingClientRect()
      const out = {}
      for (const c of chunks) {
        const node = cont.querySelector(`[data-anchor="${c.id}"]`)
        if (!node) continue
        const r = node.getBoundingClientRect()
        out[c.id] = { top: r.top - cr.top }
      }
      setPositions(out)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(containerRef.current)
    window.addEventListener('resize', measure)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure)
    }
    return () => {
      cancelled = true
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, chunks, ...deps])
  return positions
}

function ChipRail({ chunks, positions, activeId, setActiveId }) {
  return (
    <div className="chip-rail">
      {chunks.map(c => {
        const pos = positions[c.id]
        if (!pos) return null
        const dataActive =
          activeId == null ? '' : activeId === c.id ? 'on' : 'off'
        return (
          <div
            key={c.id}
            className="chip"
            data-rank={visibleRank(c.rank)}
            data-active={dataActive}
            style={{ top: pos.top + 1 }}
            onMouseEnter={() => setActiveId(c.id)}
            title={c.label}
          >
            <span className="chip-rank">{c.rank}</span>
            <span className="chip-score">{c.score.toFixed(2)}</span>
          </div>
        )
      })}
    </div>
  )
}

function DetailRail({ chunks, hasOverlaps, activeId, setActiveId }) {
  if (chunks.length === 0) return null
  return (
    <aside
      className="detail-rail"
      onMouseLeave={() => setActiveId(null)}
    >
      <h4>Child chunks · {chunks.length}</h4>
      {chunks.map(c => {
        const dataActive =
          activeId == null ? '' : activeId === c.id ? 'on' : 'off'
        return (
          <div
            key={c.id}
            className="detail-row"
            data-active={dataActive}
            onMouseEnter={() => setActiveId(c.id)}
          >
            <div className="detail-row-head">
              <span
                className="chip-rank"
                data-rank={visibleRank(c.rank)}
              >
                {c.rank}
              </span>
              <span className="detail-row-label">{c.label}</span>
              <span className="detail-row-score">{c.score.toFixed(2)}</span>
            </div>
            {!c.located && (
              <span className="detail-row-tag">(not located)</span>
            )}
          </div>
        )
      })}
      {hasOverlaps && (
        <div className="detail-rail-footer">
          Striped span = overlap between chunks.
        </div>
      )}
    </aside>
  )
}

function ReferencePanel({ source }) {
  const [activeId, setActiveId] = useState(null)
  const bodyRef = useRef(null)
  const parentContent = source.parent_content || ''

  const chunks = useMemo(() => {
    const raw = (source.matching_chunks || []).map((chunk, ix) => {
      const flatSpan = locateChunk(chunk.content || '', parentContent)
      return { chunk, flatSpan, originalIndex: ix }
    })
    raw.sort((a, b) => Number(b.chunk.score || 0) - Number(a.chunk.score || 0))
    const seenStarts = new Set()
    const out = []
    raw.forEach((r, ix) => {
      const rank = ix + 1
      const startKey = r.flatSpan ? r.flatSpan.start : null
      if (startKey != null && seenStarts.has(startKey)) {
        out.push({
          id: r.chunk.chunk_id || `c-${r.originalIndex}`,
          rank,
          score: Number(r.chunk.score || 0),
          label: makeLabel(r.chunk, rank),
          flatSpan: null,
          located: false,
        })
        return
      }
      if (startKey != null) seenStarts.add(startKey)
      out.push({
        id: r.chunk.chunk_id || `c-${r.originalIndex}`,
        rank,
        score: Number(r.chunk.score || 0),
        label: makeLabel(r.chunk, rank),
        flatSpan: r.flatSpan,
        located: !!r.flatSpan,
      })
    })
    return out
  }, [source.matching_chunks, parentContent])

  const hasOverlaps = useMemo(() => {
    if (chunks.length < 2) return false
    const segs = segmentText(parentContent, chunks)
    return segs.some(seg => seg.children.length > 1)
  }, [parentContent, chunks])

  const positions = useChipPositions(bodyRef, chunks, [])

  const topChunk = chunks[0]
  const topScore = topChunk?.score ?? 0
  const unlocatedCount = chunks.filter(c => !c.located).length
  const hasBody = parentContent.length > 0
  const filename =
    source.metadata?.filename ||
    source.metadata?.document_id ||
    'RAG document'
  const totalChunksCount = source.matching_chunks?.length ?? 0

  return (
    <div className="reference-panel">
      <div className="result-card">
        <div className="result-card-head">
          <div className="result-card-title">{filename}</div>
          {topScore > 0 && (
            <div className="result-card-pct">{formatPercent(topScore)}</div>
          )}
        </div>
        <div className="result-card-meta">
          {totalChunksCount} matching{' '}
          {totalChunksCount === 1 ? 'chunk' : 'chunks'}
        </div>
        {parentContent && (
          <div className="result-card-preview">{makePreview(parentContent)}</div>
        )}
      </div>

      <h3 className="h-eye">Reference Details</h3>
      <dl className="meta">
        <dt>Source ID</dt>
        <dd>{source.memory_id || source.id}</dd>
        <dt>Provider</dt>
        <dd>RAG Documents</dd>
        <dt>Original Query</dt>
        <dd className="meta-query">&quot;{source.query}&quot;</dd>
        {source.metadata?.filename && (
          <>
            <dt>File</dt>
            <dd>{source.metadata.filename}</dd>
          </>
        )}
        {source.metadata?.content_type && (
          <>
            <dt>Content Type</dt>
            <dd>{source.metadata.content_type}</dd>
          </>
        )}
        {source.total_parents_in_document > 1 &&
          Number.isInteger(source.parent_index) && (
            <>
              <dt>Section</dt>
              <dd>
                {source.parent_index + 1} of {source.total_parents_in_document}
              </dd>
            </>
          )}
        {source.metadata?.document_id && (
          <>
            <dt>Document ID</dt>
            <dd>{source.metadata.document_id}</dd>
          </>
        )}
        {source.metadata?.chunk_id !== undefined &&
          source.metadata?.chunk_id !== null && (
            <>
              <dt>Chunk ID</dt>
              <dd>{String(source.metadata.chunk_id)}</dd>
            </>
          )}
        <dt>Relevance Score</dt>
        <dd>
          <div className="score-row">
            <div className="score-bar">
              <div
                className="score-bar-fill"
                style={{
                  right: `${(1 - (source.relevance_score || 0)) * 100}%`,
                }}
              />
            </div>
            <span className="score-text">
              {formatPercent(source.relevance_score || 0)}
            </span>
          </div>
        </dd>
      </dl>

      {hasBody && (
        <>
          <h3 className="h-eye">Parent Passage</h3>
          <div className="reference-panel-body-grid">
            <div
              className="gutter"
              ref={bodyRef}
              onMouseLeave={() => setActiveId(null)}
            >
              <ChipRail
                chunks={chunks}
                positions={positions}
                activeId={activeId}
                setActiveId={setActiveId}
              />
              <HighlightedBody
                parentContent={parentContent}
                chunks={chunks}
                activeId={activeId}
                setActiveId={setActiveId}
              />
            </div>
            <DetailRail
              chunks={chunks}
              hasOverlaps={hasOverlaps}
              activeId={activeId}
              setActiveId={setActiveId}
            />
          </div>
          {unlocatedCount > 0 && (
            <div className="reference-panel-unlocated">
              {unlocatedCount} matching{' '}
              {unlocatedCount === 1 ? 'chunk' : 'chunks'} could not be located
              in the passage.
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ReferencePanel
